import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from "@angular/forms";
import {DatePipe, NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet} from "@angular/common";
import {IonModal} from "@ionic/angular";
import {EditorComponent} from "../editor/editor.component";
import {SharedModule} from "../../shared.module";
import {UploadImageComponent, UploadResultType} from "../upload-image/upload-image.component";
import {CommonService} from "../../service/common.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-simple-input',
  templateUrl: './simple-input.component.html',
  styleUrls: ['./simple-input.component.scss'],
  imports: [
    ReactiveFormsModule,
    DatePipe,
    NgForOf,
    NgSwitchCase,
    NgSwitch,
    NgSwitchDefault,
    NgTemplateOutlet,
    EditorComponent,
    SharedModule,
    NgIf,
    UploadImageComponent
  ],
  standalone: true
})
export class SimpleInputComponent implements OnInit {

  @ViewChild('dateBtn', {read: ElementRef}) dateBtn!: ElementRef;
  @ViewChild('editorModalRef', {static: false}) editorModalRef: IonModal;

  @Input() label: string;
  @Input() id: string;
  @Input() placeHolder: string;
  @Input() iconName: string;
  @Input() type: 'tel' | 'email' | 'password' | 'number' | 'text' | 'date' | 'time' | 'select' | 'editor' | 'link' | 'file' = 'text';
  @Input() control: FormControl;
  @Input() options: SelectOptionVO[] = [];

  @Input() min: any;
  @Input() max: any;

  @Output() inputClick = new EventEmitter<void>();

  editorControl: FormControl = new FormControl();
  passwordType: 'password' | 'text' = 'password';

  constructor(private translateService: TranslateService,
              private commonService: CommonService) {
  }

  ngOnInit() {

    if (this.type == 'date') {
      this.placeHolder = 'dd/MM/yyyy';
    }

    if (this.type == 'time') {
      this.placeHolder = 'HH:mm';
    }

    this._addValidators();
    this.editorControl.setValue(this.control.value);

  }

  onSave(){
    this.control.setValue(this.editorControl.value);
    this.editorModalRef.dismiss();
  }

  onInputClick(): void {
    if (!this.control.disabled) {
      this.inputClick.emit();
    }
  }

  onCloseEditorModal() {

    if (this.editorControl.dirty) {

      const header = this.translateService.instant('Attenzione');
      const msg = this.translateService.instant('Sicuro di voler perdere i dati non salvati?');
      const confirm = this.translateService.instant('Conferma');
      const cancel = this.translateService.instant('Annulla');

      this.commonService.presentAlertMsg(header, msg, [
        {
          text: cancel,
          handler: () => {}
        },
        {
          text: confirm,
          handler: () => {
            this.editorModalRef.dismiss();
          }
        }
      ]);

    } else {
      this.editorModalRef.dismiss();
    }


  }

  isInvalid(): boolean {
    return this.control.invalid && (this.control.dirty || this.control.touched);
  }

  togglePwdType() {
    this.passwordType = this.passwordType === 'text' ? 'password' : 'text';
  }

  getSelectedLabel(): string {
    const selectedOption = this.options.find(opt => opt.value === this.control.value);
    return selectedOption ? selectedOption.label : ''; // Ritorna la label o una stringa vuota
  }

  onSetDateTimeValue(event) {
    const value = event.detail.value;
    this.control.setValue(value);
  }

  getErrorKeys(): string[] {
    return this.control?.errors ? Object.keys(this.control.errors) : [];
  }

  showIcon() {

    const types = ['tel', 'link'];

    return types?.indexOf(this.type) != -1 || !!this.iconName;
  }

  getIconName() {

    if (this.iconName) {
      return this.iconName;
    }

    switch (this.type) {
      case 'tel':
        return 'call';
      case 'link':
        return 'link';
      default:
        return this.iconName;
    }
  }

  private _addValidators() {
    switch (this.type) {
      case 'tel':
        this.control.addValidators([this.phoneValidator()]);
        break;
      case 'link':
        this.control.addValidators([this.linkValidator()]);
        break;
    }
  }

  onChangeImage(event: UploadResultType) {
    this.control.setValue(event);
  }

  phoneValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const valid = /^(\+39)?\s?3\d{2}\s?\d{6,7}$/.test(value);
      return valid || !value ? null : {phone: true};
    };
  }

  linkValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let value = control.value;

      if (!value) {
        return null; // campo vuoto è valido
      }

      // Se manca il protocollo, aggiungilo temporaneamente per validare
      if (!/^https?:\/\//i.test(value)) {
        value = 'https://' + value;
      }

      try {
        const url = new URL(value);

        // Valida che ci sia almeno un dominio valido tipo "example.com"
        const domainPattern = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!domainPattern.test(url.hostname)) {
          return {link: true};
        }

        return null; // valido
      } catch {
        return {link: true}; // non è un URL valido
      }
    };
  }

}

export interface SelectOptionVO {
  label: string;
  value: any;
  _ref?: any;
}
